/**
 * CircuitBreaker pattern implementation to prevent continuous failed API calls
 * This helps improve resilience and prevent UI freezing when APIs are unavailable
 */
export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private isOpen = false;
  private readonly resetTimeoutMs: number;
  private readonly maxFailures: number;
  
  /**
   * Create a new CircuitBreaker
   * @param maxFailures Number of failures before opening the circuit (default: 3)
   * @param resetTimeoutMs Time in ms to wait before attempting to close the circuit (default: 30000)
   */
  constructor(
    maxFailures = 3,
    resetTimeoutMs = 30000
  ) {
    this.maxFailures = maxFailures;
    this.resetTimeoutMs = resetTimeoutMs;
  }
  
  /**
   * Execute a function with circuit breaker protection
   * @param fn Function to execute
   * @returns Promise with the function result
   * @throws Error if circuit is open or function execution fails
   */
  public async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.isOpen) {
      // Check if circuit should be reset
      const now = Date.now();
      if (now - this.lastFailureTime > this.resetTimeoutMs) {
        this.reset();
      } else {
        throw new Error('Circuit is open - too many recent failures');
      }
    }
    
    try {
      const result = await fn();
      this.recordSuccess();
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }
  
  /**
   * Check if the circuit is currently open (not allowing requests)
   * @returns boolean indicating if circuit is open
   */
  public isCircuitOpen(): boolean {
    // Check if should be reset due to timeout
    if (this.isOpen) {
      const now = Date.now();
      if (now - this.lastFailureTime > this.resetTimeoutMs) {
        this.reset();
        return false;
      }
    }
    return this.isOpen;
  }
  
  /**
   * Record a successful operation
   */
  private recordSuccess(): void {
    this.failures = 0;
    this.isOpen = false;
  }
  
  /**
   * Record a failed operation
   */
  private recordFailure(): void {
    this.failures += 1;
    this.lastFailureTime = Date.now();
    
    if (this.failures >= this.maxFailures) {
      this.isOpen = true;
      console.warn(`Circuit breaker opened after ${this.failures} failures`);
    }
  }
  
  /**
   * Reset the circuit breaker
   */
  private reset(): void {
    if (this.isOpen) {
      console.log('Circuit breaker reset - allowing requests again');
    }
    this.failures = 0;
    this.isOpen = false;
  }
} 